/**
 * Translation slice (SCRUM-1951, RTK bundle 2) — the former switch reducer
 * translationReducer.js as createSlice. Behaviour-preserving: every case answers
 * the same legacy action type string (types.js) that translationActions.js and
 * pageServices/pagesSlice.ts (GET_PAGE_BY_ID) dispatch, and returns the same
 * state — including the `error` field GET_ERRORS adds, which the initial state
 * does not declare. The actions module is unchanged, so no importer changes.
 * Characterised by tests/integration/reducers/reviewPublishingReducers.characterisation.test.js.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  TRANSLATE,
  GET_ALL_TRANSLATED_PAGES,
  DELETE_TRANSLATE_PAGE,
  GET_PAGE_BY_ID,
  GET_ERRORS,
} from "./types";

// The four fields stay `any`, as in the JSDoc this replaces: the translation
// screens (TranslatePagesContainer, UpdateTranslatePageContainer, being
// converted in SCRUM-2044) read fields no shared type declares yet
// (`target_language`, `requires_review`, `parentPage`). They narrow with them.
type TranslationState = {
  translatedpages: any[];
  pagetranslated: any;
  page: any;
  error?: any;
};

const initialState: TranslationState = {
  translatedpages: [],
  pagetranslated: {},
  page: {},
};

const translationSlice = createSlice({
  name: "translatedpages",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(GET_ALL_TRANSLATED_PAGES, (state, action: PayloadAction<TranslationState["translatedpages"], typeof GET_ALL_TRANSLATED_PAGES>) => ({
        ...state,
        translatedpages: action.payload,
      }))
      .addCase(DELETE_TRANSLATE_PAGE, (state, action: PayloadAction<string, typeof DELETE_TRANSLATE_PAGE>) => ({
        ...state,
        translatedpages: state.translatedpages.filter((page) => page._id !== action.payload),
      }))
      .addCase(TRANSLATE, (state, action: PayloadAction<TranslationState["pagetranslated"], typeof TRANSLATE>) => ({
        ...state,
        pagetranslated: action.payload,
      }))
      .addCase(GET_PAGE_BY_ID, (state, action: PayloadAction<TranslationState["page"], typeof GET_PAGE_BY_ID>) => ({
        ...state,
        page: action.payload,
      }))
      .addCase(GET_ERRORS, (state, action: PayloadAction<TranslationState["error"], typeof GET_ERRORS>) => ({
        ...state,
        error: action.payload,
      }));
  },
});

export default translationSlice.reducer;
